import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, catchError, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { RegistrationFormData, CallPlan } from '../models/attendee.model';
import { AttendeeRegistrationPayload, CallPlanVerifyResponse } from '../models/backend-response.model';

@Injectable({
  providedIn: 'root'
})
export class AttendeeService {
  private apiUrl = environment.apiUrl;
  private companyId = environment.companyId;

  constructor(private http: HttpClient) {}

  registerAttendee(eventId: number | string, attendeeData: RegistrationFormData, token?: string): Observable<number> {
    let url = `${this.apiUrl}/api/companies/${this.companyId}/events/${eventId}/attendees`;
    
    if (token) {
      url += `?token=${encodeURIComponent(token)}`;
    }
    
    const payload: AttendeeRegistrationPayload = {
      self: true,
      first_name: attendeeData.first_name || '',
      middle_name: attendeeData.middle_name || '',
      last_name: attendeeData.last_name || '',
      email: attendeeData['office.email'] || attendeeData.email || '',
      degrees: attendeeData.degrees ? [attendeeData.degrees] : [],
      other_degree: attendeeData.degrees_other || '',
      specialties: attendeeData.subspecialty ? [attendeeData.subspecialty] : [],
      other_specialties: attendeeData.subspecialty_other || '',
      npi_no: attendeeData.cats_num || '',
      address: {
        affiliation: attendeeData['office.affiliation'] || '',
        address1: attendeeData['office.address1'] || '',
        address2: attendeeData['office.address2'] || '',
        city: attendeeData['office.city'] || '',
        state: attendeeData['office.state'] || '',
        zip: attendeeData['office.zip'] || '',
        country: 'US',
        phone: attendeeData['office.phone'] || '',
        email: attendeeData['office.email'] || ''
      },
      invited_status_id: 'invited'
    };

    if (attendeeData.license && attendeeData.license_state) {
      payload.licenses = [{
        license: attendeeData.license,
        state: attendeeData.license_state
      }];
    }

    if (attendeeData.is_hcp) {
      if (attendeeData.is_hcp === 'HCP_LICENSED' || attendeeData.is_hcp === 'HCP_NOT_LICENSED') {
        payload.medical_professional = true;
        payload.hcp_flag = true;
        payload.attendee_type = 'HCP';
      } else {
        payload.medical_professional = false;
        payload.hcp_flag = false;
      }
    }

    if (attendeeData.contact_type) {
      payload.profession = attendeeData.contact_type;
    }

    if (attendeeData.call_plan_id) {
      payload.call_plan_id = Number(attendeeData.call_plan_id);
    }
    
    return this.http.post<number>(url, payload);
  }

  getCallPlan(data: { cats_num?: string; first_name?: string; last_name?: string; license?: string; license_state?: string; is_hcp?: string }): Observable<CallPlan | null> {
    const url = `${this.apiUrl}/api/companies/${this.companyId}/contacts/verify`;
    
    const contact: Record<string, unknown> = {};
    if (data.cats_num) contact['npi_no'] = data.cats_num;
    if (data.first_name) contact['first_name'] = data.first_name;
    if (data.last_name) contact['last_name'] = data.last_name;
    
    if (data.license && data.license_state) {
      contact['licenses'] = [{
        license: data.license,
        state: data.license_state
      }];
    }
    
    if (data.license_state) {
      contact['default_address'] = {
        state: data.license_state
      };
    }
    
    const payload = {
      contact,
      verifiedField: 'first_name' as const
    };
    
    return this.http.post<CallPlanVerifyResponse>(url, payload).pipe(
      map(result => {
        if (result?.verified && result.callPlan) {
          const callPlan = result.callPlan;
          return {
            id: callPlan.id,
            compass_id: callPlan.compass_id ? String(callPlan.compass_id) : String(callPlan.id),
            first_name: callPlan.first_name || data.first_name,
            last_name: callPlan.last_name || data.last_name,
            city: callPlan.city,
            state: callPlan.state,
            license_state: callPlan.license_state,
            license: callPlan.state_license_number,
            degrees: Array.isArray(callPlan.degrees) ? callPlan.degrees.join(', ') : (callPlan.degrees || ''),
            specialty: callPlan.specialty || callPlan.subspecialty,
            contact_type: callPlan.contact_type,
            affiliation: callPlan.affiliation,
            address1: callPlan.address1,
            zip: callPlan.zip
          };
        }
        return null;
      }),
      catchError(() => of(null))
    );
  }

  saveRegistrationAnswers(eventId: number | string, attendeeId: number, answers: Record<number, any>, token?: string): Observable<any> {
    let url = `${this.apiUrl}/api/companies/${this.companyId}/events/${eventId}/registration_answers`;
    if (token) {
      url += `?token=${encodeURIComponent(token)}`;
    }
    const payload = {
      attendee_id: attendeeId,
      answers: answers,
      update: true
    };
    return this.http.post<any>(url, payload);
  }
}

